/**
 * Social Trends Configuration Editor
 *
 * Embedded component for configuring TrueTrends social media integration.
 * Includes:
 * - Master enable/disable toggle
 * - API configuration with connection test
 * - Platform selection
 * - Custom topic management
 * - Spike detection settings
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Button,
  Chip,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  IconButton,
  CircularProgress,
  Collapse,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendsIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  PlayArrow as TestIcon,
} from '@mui/icons-material';
import { socialConfigApi } from '../services/socialConfigApi';
import {
  SocialTrendsConfig,
  CustomTopic,
  SOCIAL_PLATFORMS,
  SYNC_FREQUENCIES,
  DATA_RETENTION_OPTIONS,
  SocialPlatformsConfig,
} from '../types/socialConfig';

interface SocialTrendsConfigEditorProps {
  projectId?: string;
  config: SocialTrendsConfig;
  onChange: (config: SocialTrendsConfig) => void;
}

export const SocialTrendsConfigEditor: React.FC<SocialTrendsConfigEditorProps> = ({
  projectId,
  config,
  onChange,
}) => {
  const theme = useTheme();
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionResult, setConnectionResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [newTopicName, setNewTopicName] = useState('');
  const [newTopicKeywords, setNewTopicKeywords] = useState('');
  const [apiKeyInput, setApiKeyInput] = useState('');

  const handleToggle = (field: keyof SocialTrendsConfig) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({ ...config, [field]: event.target.checked });
  };

  const handleChange = <K extends keyof SocialTrendsConfig>(
    field: K,
    value: SocialTrendsConfig[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  const handlePlatformToggle = (platformId: keyof SocialPlatformsConfig) => {
    const newPlatforms = {
      ...config.social_platforms_enabled,
      [platformId]: !config.social_platforms_enabled[platformId],
    };
    onChange({ ...config, social_platforms_enabled: newPlatforms });
  };

  const handleTestConnection = async () => {
    if (!projectId) return;

    setTestingConnection(true);
    setConnectionResult(null);

    try {
      const result = await socialConfigApi.testConnection(projectId);
      setConnectionResult({
        success: result.success,
        message: result.success
          ? `Connected successfully (${result.response_time_ms}ms)`
          : result.message,
      });
    } catch (error: any) {
      setConnectionResult({
        success: false,
        message: error.response?.data?.detail || 'Connection test failed',
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleAddTopic = () => {
    if (!newTopicName.trim() || !newTopicKeywords.trim()) return;

    const keywords = newTopicKeywords
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);
    if (keywords.length === 0) return;

    const newTopic: CustomTopic = {
      id: `temp-${Date.now()}`,
      name: newTopicName.trim(),
      keywords,
      enabled: true,
    };

    onChange({
      ...config,
      social_custom_topics: [...config.social_custom_topics, newTopic],
    });

    setNewTopicName('');
    setNewTopicKeywords('');
  };

  const handleDeleteTopic = (topicId: string) => {
    onChange({
      ...config,
      social_custom_topics: config.social_custom_topics.filter(
        (t) => t.id !== topicId
      ),
    });
  };

  const handleToggleTopic = (topicId: string) => {
    onChange({
      ...config,
      social_custom_topics: config.social_custom_topics.map((t) =>
        t.id === topicId ? { ...t, enabled: !t.enabled } : t
      ),
    });
  };

  return (
    <Box>
      {/* Master Toggle */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TrendsIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">Social Trends Integration</Typography>
            <Typography variant="body2" color="text.secondary">
              Connect to TrueTrends API for real-time social media trend analysis
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={config.social_trends_enabled}
                onChange={handleToggle('social_trends_enabled')}
                color="primary"
              />
            }
            label=""
          />
        </Box>
      </Paper>

      <Collapse in={config.social_trends_enabled}>
        {/* API Configuration */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            TrueTrends API Configuration
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API URL"
                value={config.truetrends_api_url || ''}
                onChange={(e) =>
                  handleChange('truetrends_api_url', e.target.value || null)
                }
                placeholder="https://api.truetrends.com/v1"
                helperText="TrueTrends API endpoint URL"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                type="password"
                label="API Key"
                value={apiKeyInput}
                placeholder={
                  config.truetrends_api_key_configured
                    ? '••••••••••••'
                    : 'Enter API key'
                }
                helperText={
                  config.truetrends_api_key_configured
                    ? 'API key is configured. Enter new value to update.'
                    : 'API key will be stored securely'
                }
                onChange={(e) => setApiKeyInput(e.target.value)}
                onBlur={() => {
                  // Only update if a new value was entered
                  if (apiKeyInput.trim()) {
                    // The parent component should handle persisting this
                    // through the separate social-config PATCH endpoint
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleTestConnection}
                disabled={testingConnection || !projectId}
                startIcon={
                  testingConnection ? <CircularProgress size={18} /> : <TestIcon />
                }
                sx={{ height: 56 }}
              >
                {testingConnection ? 'Testing...' : 'Test Connection'}
              </Button>
            </Grid>
          </Grid>

          {connectionResult && (
            <Alert
              severity={connectionResult.success ? 'success' : 'error'}
              sx={{ mt: 2 }}
              icon={connectionResult.success ? <SuccessIcon /> : <ErrorIcon />}
            >
              {connectionResult.message}
            </Alert>
          )}
        </Paper>

        {/* Platform Selection */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Enabled Platforms
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which social media platforms to monitor for trends
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {SOCIAL_PLATFORMS.map((platform) => (
              <Chip
                key={platform.id}
                label={`${platform.icon} ${platform.label}`}
                onClick={() => handlePlatformToggle(platform.id)}
                color={
                  config.social_platforms_enabled[platform.id]
                    ? 'primary'
                    : 'default'
                }
                variant={
                  config.social_platforms_enabled[platform.id]
                    ? 'filled'
                    : 'outlined'
                }
                sx={{ fontSize: '0.9rem', py: 2 }}
              />
            ))}
          </Box>
        </Paper>

        {/* Sync Settings */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Sync Settings
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Sync Frequency</InputLabel>
                <Select
                  value={config.social_sync_frequency_hours}
                  label="Sync Frequency"
                  onChange={(e) =>
                    handleChange(
                      'social_sync_frequency_hours',
                      e.target.value as number
                    )
                  }
                >
                  {SYNC_FREQUENCIES.map((freq) => (
                    <MenuItem key={freq.value} value={freq.value}>
                      {freq.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Data Retention</InputLabel>
                <Select
                  value={config.social_data_retention_days}
                  label="Data Retention"
                  onChange={(e) =>
                    handleChange(
                      'social_data_retention_days',
                      e.target.value as number
                    )
                  }
                >
                  {DATA_RETENTION_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Spike Detection */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Spike Detection
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure how the system detects unusual spikes in social media
            activity
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography gutterBottom>
                Spike Threshold: {config.social_spike_threshold}x
              </Typography>
              <Slider
                value={config.social_spike_threshold}
                onChange={(_, value) =>
                  handleChange('social_spike_threshold', value as number)
                }
                min={1.5}
                max={5}
                step={0.5}
                marks={[
                  { value: 1.5, label: '1.5x' },
                  { value: 2.5, label: '2.5x' },
                  { value: 5, label: '5x' },
                ]}
              />
              <Typography variant="caption" color="text.secondary">
                Activity must exceed baseline by this factor to trigger spike
                alert
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography gutterBottom>
                Lookback Window: {config.social_spike_lookback_days} days
              </Typography>
              <Slider
                value={config.social_spike_lookback_days}
                onChange={(_, value) =>
                  handleChange('social_spike_lookback_days', value as number)
                }
                min={3}
                max={14}
                step={1}
                marks={[
                  { value: 3, label: '3d' },
                  { value: 7, label: '7d' },
                  { value: 14, label: '14d' },
                ]}
              />
              <Typography variant="caption" color="text.secondary">
                Number of days used to calculate baseline activity
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Custom Topics */}
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Custom Topics
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define custom keyword groups to track specific topics
          </Typography>

          {/* Existing Topics */}
          {config.social_custom_topics.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {config.social_custom_topics.map((topic) => (
                <Paper
                  key={topic.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    opacity: topic.enabled ? 1 : 0.6,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight={500}>{topic.name}</Typography>
                      <Chip
                        size="small"
                        label={topic.enabled ? 'Active' : 'Inactive'}
                        color={topic.enabled ? 'success' : 'default'}
                      />
                    </Box>
                    <Box
                      sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}
                    >
                      {topic.keywords.map((kw, idx) => (
                        <Chip key={idx} label={kw} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={topic.enabled}
                        onChange={() => handleToggleTopic(topic.id)}
                        size="small"
                      />
                    }
                    label=""
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteTopic(topic.id)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Paper>
              ))}
            </Box>
          )}

          {config.social_custom_topics.length === 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              No custom topics configured. Add topics below to track specific
              keywords.
            </Alert>
          )}

          {/* Add New Topic */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" fontWeight={500} gutterBottom>
            Add New Topic
          </Typography>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Topic Name"
                value={newTopicName}
                onChange={(e) => setNewTopicName(e.target.value)}
                placeholder="e.g., Traffic Issues"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Keywords (comma-separated)"
                value={newTopicKeywords}
                onChange={(e) => setNewTopicKeywords(e.target.value)}
                placeholder="e.g., traffic, congestion, roads"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleAddTopic}
                disabled={!newTopicName.trim() || !newTopicKeywords.trim()}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default SocialTrendsConfigEditor;
